import ShapeSelect from '@/shared/components/Select/ShapeSelect'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ImageUploadButton from '@/shared/components/ImageUploadButton/ImageUploadButton'
import ColorPicker from '@/shared/components/ColorPicker/ColorPicker'
import { HSVA } from '@/features/mobile-app/store/useColorStore'
import IconButton from '@/shared/components/IconButton/IconButton'
import CheckBox from '@/shared/components/CheckBox/CheckBox'
import useMobileAppManagementQuery from '@/features/mobile-app/hooks/useMobileAppManagementQuery'
import { hsvaToHex, hsvaToRgbaString } from '@uiw/color-convert'
import { useImageSlots } from '@/shared/store/useImageSlots'
import TooltipButton from '@/shared/components/TooltipButton/TooltipButton'
import TextField from '@/shared/components/TextField/TextField'
import Select from '@/shared/components/Select/Select'
import { useReactToPrint } from 'react-to-print'
import { ShapeType } from '@/shared/api/BaseAPI.model'
import Button from '@/shared/components/Button/Button'
import { selectValueType } from '@/shared/components/DynamicForm/DynamicForm'
import styles from './TableCardForm.module.scss'
import getTableCardPreview from '../../helpers/getTableCardPreview'
import { buildTableQrValue } from '../../helpers/buildTableQrValue'
import { useTableCardStore } from '@/features/table-cards/store/useTableCardStore'
import getTableCardImageSize from '../../helpers/getTableCardImageSize'
import {
  qrCodeCardsPostPayloadModel,
  qrCodeCardsPutPayloadModel
} from '../../api/TableCardsManagementAPI.model'

const MM_PER_PX = 25.4 / 96

const defaultSquareSizes = {
  smallSticker: {
    width: 50,
    height: 50,
    label: 'Small Sticker (50x50 mm)',
    value: 'small'
  },
  coaster: {
    width: 90,
    height: 90,
    label: 'Coaster (90x90 mm)',
    value: 'medium'
  },
  largerSign: {
    width: 120,
    height: 120,
    label: 'Larger Sign (120x120 mm)',
    value: 'large'
  },
  customSize: {
    width: null,
    height: null,
    label: 'Custom Size',
    value: 'custom'
  }
}

const defaultCircleSizes = {
  small: { diameter: 60, label: 'Small Circle (60 mm Ø)', value: 'small' },
  mediumCoaster: {
    diameter: 90,
    label: 'Medium Coaster (90 mm Ø)',
    value: 'medium'
  },
  largeCoaster: {
    diameter: 120,
    label: 'Large Coaster (120 mm Ø)',
    value: 'large'
  },
  customSize: { diameter: null, label: 'Custom Size', value: 'custom' }
}

const defaultVerticalRectangleSizes = {
  a7: { width: 74, height: 105, label: 'A7 (74×105 mm)', value: 'small' },
  postcardPortrait: {
    width: 100,
    height: 150,
    label: 'Postcard Portrait (100×150 mm)',
    value: 'medium'
  },
  a6: {
    width: 105,
    height: 148,
    label: 'A6 Portrait (105×148 mm)',
    value: 'large'
  },
  a5Portrait: {
    width: 148,
    height: 210,
    label: 'A5 Portrait (105×148 mm)',
    value: 'xlarge'
  },
  customSize: {
    width: null,
    height: null,
    label: 'Custom Size',
    value: 'custom'
  }
}

const defaultHorizontalRectangleSizes = {
  businessCard: {
    width: 85,
    height: 55,
    label: 'Business Card (85×55 mm)',
    value: 'small'
  },
  postcardLandscape: {
    width: 150,
    height: 100,
    label: 'Postcard Landscape (150×100 mm)',
    value: 'medium'
  },
  a6LandScape: {
    width: 148,
    height: 105,
    label: 'A6 Landscape (148×105 mm)',
    value: 'large'
  },
  a5Landscape: {
    width: 210,
    height: 148,
    label: 'A5 Landscape (210×148 mm)',
    value: 'xlarge'
  },
  customSize: {
    width: null,
    height: null,
    label: 'Custom Size',
    value: 'custom'
  }
}

const getDefaultSizesByShape = (shape: ShapeType) => {
  switch (shape) {
    case 'square':
      return defaultSquareSizes
    case 'circle':
      return defaultCircleSizes
    case 'verticalRectangle':
      return defaultVerticalRectangleSizes
    case 'horizontalRectangle':
      return defaultHorizontalRectangleSizes
    default:
      return defaultSquareSizes
  }
}

interface TableCardFormProps {
  submitFunction: (
    payload: qrCodeCardsPostPayloadModel | qrCodeCardsPutPayloadModel
  ) => void
  noEdit?: boolean
}

function TableCardForm(props: TableCardFormProps) {
  const { submitFunction, noEdit = false } = props
  const { t } = useTranslation()
  const {
    id,
    title,
    tableNumber,
    tableId,
    setTitle,
    description,
    setDescription,
    shapeValue,
    setShapeValue,
    backgroundColor,
    setBackgroundColor,
    contentColor,
    setContentColor,
    contrastColor,
    setContrastColor,
    withNfc,
    setWithNfc,
    languageCode,
    setLanguageCode,
    alignment,
    setAlignment,
    backgroundImage,
    setBackgroundImage,
    widthValue,
    setWidthValue,
    heightValue,
    setHeightValue,
    diameterValue,
    setDiameterValue,
    tableIds,
    reset
  } = useTableCardStore()

  const { logoImage } = useMobileAppManagementQuery()
  const logoImageUrl = `${logoImage.data?.imageUrl}?t=${logoImage.data?.version}`

  type DefaultSize = {
    width?: number
    height?: number
    diameter?: number
    label: string
    value: string
  }

  const [selectedDefaultSize, setSelectedDefaultSize] = useState<DefaultSize>(
    () =>
      Object.values(getDefaultSizesByShape(shapeValue)).find(
        size =>
          (size.width === widthValue && size.height === heightValue) ||
          size.diameter === diameterValue
      ) ||
      Object.values(getDefaultSizesByShape(shapeValue))[
        Object.values(getDefaultSizesByShape(shapeValue)).length - 1
      ]
  )

  const slotId = `table-card-background-image${id ? `-${id}` : ''}`
  const slot = useImageSlots(s => s.slots[slotId])
  const { clearAll } = useImageSlots(s => s)

  useEffect(() => {
    if (!slot?.croppedUrl && !slot?.originalUrl) return

    const nextImage = slot.croppedUrl ?? slot.originalUrl ?? null

    if (nextImage && nextImage !== backgroundImage) {
      setBackgroundImage(nextImage)
    }
  }, [slot?.croppedUrl, slot?.originalUrl, setBackgroundImage])

  const contentRef = useRef<HTMLDivElement>(null)

  const reactToPrintFn = useReactToPrint({
    contentRef
  })

  const effectiveSizePx = useMemo(() => {
    const isCustom = selectedDefaultSize?.value === 'custom'

    if (shapeValue === 'circle') {
      const dMm =
        !isCustom && selectedDefaultSize?.diameter != null
          ? selectedDefaultSize.diameter
          : diameterValue
      const px = dMm / MM_PER_PX
      return { w: px, h: px }
    }

    if (shapeValue === 'square') {
      const wMm =
        !isCustom && selectedDefaultSize?.width != null
          ? selectedDefaultSize.width
          : widthValue
      const px = wMm / MM_PER_PX
      return { w: px, h: px }
    }

    const wMm =
      !isCustom && selectedDefaultSize?.width != null
        ? selectedDefaultSize.width
        : widthValue
    const hMm =
      !isCustom && selectedDefaultSize?.height != null
        ? selectedDefaultSize.height
        : heightValue
    return { w: wMm / MM_PER_PX, h: hMm / MM_PER_PX }
  }, [shapeValue, selectedDefaultSize, diameterValue, widthValue, heightValue])

  const tableCardPreview = useMemo(
    () =>
      getTableCardPreview({
        logoImageUrl,
        shape: shapeValue,
        withNfc,
        languageCode,
        align: alignment,
        backgroundColorValue: hsvaToHex(backgroundColor),
        contentColor: hsvaToHex(contentColor),
        contrastColor: hsvaToRgbaString(contrastColor),
        backgroundImage,
        width: effectiveSizePx.w,
        height: effectiveSizePx.h,
        tableNumber,
        qrCodeValue: buildTableQrValue(tableId)
      }),
    [
      logoImageUrl,
      shapeValue,
      withNfc,
      languageCode,
      alignment,
      backgroundColor,
      contentColor,
      contrastColor,
      backgroundImage,
      effectiveSizePx.w,
      effectiveSizePx.h,
      tableNumber,
      tableId
    ]
  )

  const { width, height } = getTableCardImageSize(shapeValue)

  const onShapeChange = (next: ShapeType) => {
    if (next === shapeValue) return

    setShapeValue(next)
    setBackgroundImage(null)

    const defaults = Object.values(getDefaultSizesByShape(next))[0]
    setSelectedDefaultSize(defaults)

    if (defaults.value !== 'custom') {
      if (next === 'circle') {
        setDiameterValue(defaults.diameter)
      } else {
        setWidthValue(defaults.width)
        setHeightValue(defaults.height)
      }
    }
  }

  const onSizeChange = (val: selectValueType) => {
    if (Array.isArray(val)) return
    const sizes = getDefaultSizesByShape(shapeValue)
    const next = Object.values(sizes).find(s => s.value === val?.value) || null
    setSelectedDefaultSize(next)

    if (!next || next.value === 'custom') return
    if (shapeValue === 'circle') {
      setDiameterValue(next.diameter ?? diameterValue)
    } else {
      setWidthValue(next.width ?? widthValue)
      setHeightValue(next.height ?? heightValue)
    }
  }

  const tableCardBackgroundImagePicker = (
    <div className={styles.tableCardFormGroup}>
      <ImageUploadButton
        imageShape={shapeValue === 'circle' ? 'circle' : 'rectangle'}
        label={t('TABLE_CARD_FORM.IMAGE_LABEL')}
        slotId={slotId}
        imageUrl={backgroundImage ?? undefined}
        // The store keeps its own copy of the blob URL, and clearing the slot
        // revokes it, without this the tile would render a dead `blob:` and,
        // because the preview stays truthy, refuse to open the picker again.
        onDelete={() => setBackgroundImage(null)}
        imageSize={{ width: width * 0.8, height: height * 0.8 }}
        stencilSize={{ width: width * 0.6, height: height * 0.6 }}
        isVertical={shapeValue === 'verticalRectangle'}
      />
    </div>
  )

  const tableCardColorPicker = (
    <div className={styles.tableCardFormGroup}>
      <ColorPicker
        key="content"
        label={t('TABLE_CARD_FORM.CONTENT_COLOR')}
        color={contentColor}
        setColor={(color: HSVA) => setContentColor(color)}
      />
      <ColorPicker
        key="background"
        label={t('TABLE_CARD_FORM.BACKGROUND_COLOR')}
        color={backgroundColor}
        setColor={(color: HSVA) => setBackgroundColor(color)}
      />
      {backgroundImage && (
        <ColorPicker
          key="contrast"
          label={t('TABLE_CARD_FORM.CONTRAST_COLOR')}
          color={contrastColor}
          setColor={(color: HSVA) => {
            const roundedColor = { ...color, a: parseFloat(color.a.toFixed(2)) }
            setContrastColor(roundedColor)
          }}
          showAlpha
        />
      )}
    </div>
  )

  const tableCardAdditionalInfo = (
    <div className={styles.tableCardFormGroup}>
      <CheckBox
        label={t('TABLE_CARD_FORM.ADD_NFC_TAG')}
        onChange={() => setWithNfc(!withNfc)}
        value={withNfc}
      />
      <CheckBox
        label={t('TABLE_CARD_FORM.IN_POLISH')}
        onChange={() => setLanguageCode(languageCode === 'en' ? 'pl' : 'en')}
        value={languageCode === 'pl'}
      />
    </div>
  )

  const [currentWidthValue, setCurrentWidthValue] = useState(widthValue)
  const [currentHeightValue, setCurrentHeightValue] = useState(heightValue)
  const [currentDiameterValue, setCurrentDiameterValue] =
    useState(diameterValue)

  useEffect(() => {
    setCurrentWidthValue(widthValue)
    setCurrentHeightValue(heightValue)
    setCurrentDiameterValue(diameterValue)
  }, [widthValue, heightValue, diameterValue])

  const tableCardSizeSelector = (
    <div className={`${styles.tableCardFormGroup} ${styles.additionalGap}`}>
      <Select
        minWidth="15rem"
        label={t('TABLE_CARD_FORM.SELECT_SIZE')}
        focused
        hideClearIcon
        value={
          selectedDefaultSize
            ? {
                value: selectedDefaultSize.value,
                label: selectedDefaultSize.label
              }
            : null
        }
        options={Object.values(getDefaultSizesByShape(shapeValue)).map(
          sizeObject => ({
            label: sizeObject.label,
            value: sizeObject.value
          })
        )}
        onChange={onSizeChange}
      />
      {shapeValue === 'circle' ? (
        <TextField
          type="number"
          label={t('TABLE_CARD_FORM.DIAMETER')}
          focused
          disabled={selectedDefaultSize?.value !== 'custom'}
          value={String(currentDiameterValue)}
          onChange={e => setCurrentDiameterValue(Number(e.target.value))}
        />
      ) : (
        <>
          <TextField
            type="number"
            label={t('TABLE_CARD_FORM.WIDTH')}
            focused
            disabled={selectedDefaultSize?.value !== 'custom'}
            value={String(currentWidthValue)}
            onChange={e => setCurrentWidthValue(Number(e.target.value))}
          />
          <TextField
            type="number"
            label={t('TABLE_CARD_FORM.HEIGHT')}
            focused
            disabled={selectedDefaultSize?.value !== 'custom'}
            value={String(currentHeightValue)}
            onChange={e => setCurrentHeightValue(Number(e.target.value))}
          />
        </>
      )}
      <Button
        color="primary"
        variant="contained"
        label={t('TABLE_CARD_FORM.APPLY_SIZE')}
        onClick={() => {
          setWidthValue(currentWidthValue)
          setHeightValue(currentHeightValue)
          setDiameterValue(currentDiameterValue)
        }}
        disabled={
          selectedDefaultSize?.value !== 'custom' ||
          (currentWidthValue === widthValue &&
            currentHeightValue === heightValue &&
            currentDiameterValue === diameterValue)
        }
      />
    </div>
  )
  const tableCardShapesAndAlignment = (
    <div className={styles.tableCardFormGroupRow}>
      <ShapeSelect onChange={onShapeChange} value={shapeValue} />
      <div className={styles.tableCardFormGroupRow}>
        {shapeValue === 'horizontalRectangle' ||
        shapeValue === 'verticalRectangle' ? (
          <div className={styles.tableCardFormGroupRow}>
            <IconButton
              color="primary"
              icon="icon-big las la-align-left"
              onClick={() => setAlignment('left')}
              disabled={alignment === 'left'}
            />
            <IconButton
              color="primary"
              icon="icon-big las la-align-center"
              onClick={() => setAlignment('center')}
              disabled={alignment === 'center'}
            />
          </div>
        ) : null}
      </div>
    </div>
  )

  const tableCardTitleAndDescription = (
    <div className={`${styles.tableCardFormGroup} ${styles.additionalGap}`}>
      <TextField
        type="text"
        label={t('TABLE_CARD_FORM.TITLE_LABEL')}
        focused
        value={title}
        onChange={e => setTitle(e.target.value)}
      />
      <TextField
        type="text-area"
        multiline
        lines={3}
        label={t('TABLE_CARD_FORM.DESCRIPTION_LABEL')}
        focused
        value={description}
        onChange={e => setDescription(e.target.value)}
      />
    </div>
  )

  const handleSubmit = () => {
    let payload: qrCodeCardsPostPayloadModel | qrCodeCardsPutPayloadModel = {
      id: id || undefined,
      title,
      description,
      shape: shapeValue,
      alignment,
      withNfc,
      languageCode,
      contentColor: hsvaToRgbaString(contentColor),
      contrastColor: hsvaToRgbaString(contrastColor),
      backgroundColor: hsvaToRgbaString(backgroundColor),
      tableIds
    }

    if (shapeValue === 'circle') {
      payload = { ...payload, diameter: diameterValue }
    } else {
      payload = { ...payload, width: widthValue, height: heightValue }
    }
    if (backgroundImage && backgroundImage.startsWith('blob:')) {
      const bgImageFile = slot.originalFile
      if (bgImageFile) {
        payload = { ...payload, backgroundImage: bgImageFile }
      }
    }
    submitFunction(payload)
  }

  const handleClear = () => {
    reset()
    clearAll()
  }

  return (
    <div className={styles.tableCardFormWrapper}>
      <div ref={contentRef} className={styles.tableCardPreviewContainer}>
        <div
          className={styles.tableCardPreviewScaled}
          style={{
            zoom: Math.min(736 / effectiveSizePx.w, 562 / effectiveSizePx.h, 1)
          }}
        >
          {tableCardPreview}
        </div>
      </div>

      {noEdit ? null : (
        <div className={styles.tableCardForm}>
          <div className={styles.tableCardButtonGroup}>
            <TooltipButton icon="icon-big las la-image">
              {tableCardBackgroundImagePicker}
            </TooltipButton>
            <TooltipButton icon="icon-big las la-palette">
              {tableCardColorPicker}
            </TooltipButton>
            <TooltipButton icon="icon-big las la-info-circle">
              {tableCardAdditionalInfo}
            </TooltipButton>
            <TooltipButton icon="icon-big las la-ruler">
              {tableCardSizeSelector}
            </TooltipButton>
            <TooltipButton icon="icon-big las la-shapes">
              {tableCardShapesAndAlignment}
            </TooltipButton>
            <TooltipButton icon="icon-big las la-font">
              {tableCardTitleAndDescription}
            </TooltipButton>
            <IconButton
              icon="icon-big las la-file"
              onClick={reactToPrintFn}
              color="primary"
            />
          </div>
        </div>
      )}

      {noEdit ? null : (
        <div className={styles.tableCardFormButtonContainer}>
          <Button
            color="primary"
            variant="contained"
            label={t('COMMON.SAVE')}
            onClick={handleSubmit}
          />
          <Button
            color="secondary"
            variant="contained"
            label={t('COMMON.CLEAR')}
            onClick={handleClear}
          />
        </div>
      )}
    </div>
  )
}

export default TableCardForm
